import React from "react";
import {
  Loader2,
  CheckCircle2,
  AlertTriangle,
  FileText,
  X,
} from "lucide-react";
import { UploadItem } from "../../contexts/UploadContext";
import { useAnimatedProgress } from "./useAnimatedProgress";

interface UploadWidgetItemProps {
  upload: UploadItem;
  onNavigate: () => void;
  onDismiss: () => void;
}

const UploadWidgetItem: React.FC<UploadWidgetItemProps> = ({
  upload,
  onNavigate,
  onDismiss,
}) => {
  const getStatusIcon = () => {
    switch (upload.status) {
      case "uploading":
      case "processing":
      case "pending":
        return <Loader2 className="animate-spin text-sky-500" size={20} />;
      case "complete":
        return <CheckCircle2 className="text-green-500" size={20} />;
      case "error":
        return <AlertTriangle className="text-red-500" size={20} />;
      default:
        return <FileText className="text-gray-400" size={20} />;
    }
  };

  const isActive =
    upload.status === "uploading" ||
    upload.status === "processing" ||
    upload.status === "pending";
  const isFinished = upload.status === "complete" || upload.status === "error";

  const animatedProgress = useAnimatedProgress(upload.progress, isActive);

  // Build a display string that uses the smooth animated number instead of
  // the raw milestone percentage coming from the backend.
  const displayStep = (() => {
    if (!isActive) return upload.currentStep;
    if (upload.status === "uploading") return upload.currentStep; // "Uploading 1 of 3..."
    // Replace the backend percentage with the animated one
    if (upload.currentStep.includes("%")) {
      return `Processing... ${animatedProgress}%`;
    }
    return upload.currentStep;
  })();

  return (
    <div className="px-4 py-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50">
      <div className="flex items-center gap-3">
        {getStatusIcon()}

        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">
            {upload.documentName}
          </p>
          <p
            className={`text-xs ${
              upload.status === "error" ? "text-red-500" : "text-gray-500"
            }`}
          >
            {upload.error || displayStep}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {upload.status === "complete" && upload.documentId && (
            <button
              onClick={onNavigate}
              className="text-sky-500 hover:text-sky-600 text-sm font-medium"
            >
              View
            </button>
          )}

          {isFinished && (
            <button
              onClick={onDismiss}
              className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100"
              title="Dismiss"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {isActive && (
        <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
          <div
            className="h-1.5 rounded-full bg-sky-500 transition-all duration-200 ease-out"
            style={{ width: `${animatedProgress}%` }}
          />
        </div>
      )}
    </div>
  );
};

export default UploadWidgetItem;
